import { confirmAccountDeletion } from "@serophin/nottldr-shared";
import { serophinContactUrl } from "@serophin/shared";
import { useEffect, useState } from "preact/hooks";
import type { PageProps } from "../pageProps";

type Status = "processing" | "redirecting" | "success" | "error";

const SIGN_OUT_DELAY_MS = 10000;
const LINK = "underline hover:text-neutral-600 dark:hover:text-neutral-300";
const TITLE = "text-2xl font-semibold mb-4 text-neutral-900 dark:text-neutral-100";
const SPINNER =
  "w-6 h-6 border-2 border-neutral-200 border-t-neutral-900 dark:border-neutral-700 dark:border-t-neutral-100 rounded-full animate-spin mx-auto mb-4";

const Processing = () => (
  <>
    <div class={SPINNER} />
    <h1 class={TITLE}>Processing Request</h1>
    <p class="text-neutral-500 dark:text-neutral-400">Please wait while we process your account deletion request...</p>
  </>
);

const Redirecting = ({ deepLink }: { deepLink: string }) => (
  <>
    <div class={SPINNER} />
    <h1 class={TITLE}>Opening App</h1>
    <p class="text-neutral-500 dark:text-neutral-400 mb-6">Redirecting you to the !tldr app to complete account deletion...</p>
    <p class="text-sm text-neutral-400 dark:text-neutral-500 mb-4">If the app doesn't open automatically, tap the button below:</p>
    <a href={deepLink} class="inline-block px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors">
      Open in App
    </a>
  </>
);

const Success = () => (
  <>
    <h1 class={TITLE}>Request Received</h1>
    <p class="text-neutral-500 dark:text-neutral-400 mb-3">Your account and all associated data will be permanently deleted.</p>
    <p class="text-neutral-500 dark:text-neutral-400 mb-6">You will receive a confirmation email once the deletion is complete.</p>
    <p class="text-sm text-neutral-400 dark:text-neutral-500">
      If you don't receive a confirmation within 24 hours, please{" "}
      <a href={serophinContactUrl("nottldr")} class={LINK}>
        contact us
      </a>
      .
    </p>
    <p class="text-sm text-neutral-400 dark:text-neutral-500 mt-4">Redirecting to home page...</p>
  </>
);

const Failure = ({ message }: { message: string }) => (
  <>
    <h1 class={TITLE}>Unable to Process Request</h1>
    <p class="text-red-600 dark:text-red-400 mb-4">{message}</p>
    <p class="text-neutral-500 dark:text-neutral-400">
      Please request a new deletion link from your{" "}
      <a href="/profile/settings" class={LINK}>
        account settings
      </a>
      .
    </p>
  </>
);

export const AuthConfirmDeletionPage = (_props: PageProps) => {
  const [status, setStatus] = useState<Status>("processing");
  const [message, setMessage] = useState("");
  const [deepLink, setDeepLink] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") ?? "";
    if (params.get("source") === "mobile") {
      const link = `nottldr://auth/confirm-deletion?token=${encodeURIComponent(token)}`;
      setDeepLink(link);
      setStatus("redirecting");
      window.location.href = link;
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    confirmAccountDeletion(token)
      .then(() => {
        setStatus("success");
        timer = setTimeout(() => window.location.assign("/auth/signout"), SIGN_OUT_DELAY_MS);
      })
      .catch((error: unknown) => {
        setMessage((error as { message?: string }).message || "The deletion link is invalid or has expired.");
        setStatus("error");
      });
    return () => clearTimeout(timer);
  }, []);

  return (
    <div class="flex justify-center items-center min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div class="text-center p-10 max-w-md">
        {status === "processing" ? <Processing /> : null}
        {status === "redirecting" ? <Redirecting deepLink={deepLink} /> : null}
        {status === "success" ? <Success /> : null}
        {status === "error" ? <Failure message={message} /> : null}
      </div>
    </div>
  );
};
